import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import {
  BookOpen,
  Building,
  Calendar,
  FileText,
  GraduationCap,
  Pencil,
  Plus,
} from "lucide-react";
import { useState } from "react";
import { fileToBase64 } from "../../../api/authService";
import professionalProfileApi from "../../../api/professionalProfile";
import Modal from "../../../components/professional/Modal";
import CertificatePreview from "../../../components/professional/CertificatePreview";
import EmptyQualificationCard from "./EmptyQualificationCard";
import QualificationForm from "./QualificationsForm";
import CustomLoader from "../../../components/common/CustomLoader";
import * as Sentry from '@sentry/react'

const QualificationsTab = ({
  qualifications,
  onModification,
  onDocumentUpload,
  setNotification,
  dropDownOptions
}) => {
  const [open, setOpen] = useState(false);
  const [selectedQualification, setSelectedQualification] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleOpen = (qualification = null) => {
    setSelectedQualification(qualification);
    setOpen(true);
  };

  const handleClose = () => {
    setSelectedQualification(null);
    setOpen(false);
  };

  const handleSave = async (data) => {
    setLoading(true);
    try {
      let payload = { ...data };
      let hasNewCertificate = false;

      if (payload.certificate_upload instanceof File) {
        // New certificate file - sync to Supporting Documents
        try {
          await professionalProfileApi.createDocuments({
            category: "educational_documents",
            files: [payload.certificate_upload],
          });
          hasNewCertificate = true;
        } catch (docErr) {
          console.error("Error syncing educational document:", docErr);
        }
        
        // Convert to base64 for the qualification record
        payload.certificate_upload = await fileToBase64(
          payload.certificate_upload
        );
      } else if (typeof payload.certificate_upload === "string") {
        // Existing file - don't send it, keep the existing one
        delete payload.certificate_upload;
      } else if (payload.certificate_upload === null) {
        payload.certificate_upload = null;
      }
      
      if (selectedQualification) {
        const res = await professionalProfileApi.updateSingleResource(
          data.id,
          "qualification",
          payload
        );
        if (res.status === 200) {
          onModification();
          // Trigger document upload refresh if new certificate was synced
          if (hasNewCertificate) {
            onDocumentUpload?.();
          }
          setNotification("Profile updated successfully", "success");
        }
      } else {
        delete payload.id;
        const res = await professionalProfileApi.updateProfessionalProfile({
          qualifications: [payload],
        });
        if (res.status === 200) {
          onModification();
          // Trigger document upload refresh if new certificate was synced
          if (hasNewCertificate) {
            onDocumentUpload?.();
          }
          setNotification("Test qualification add", "success");
        }
      }
      handleClose();
    } catch (error) {
      Sentry.captureException(error, 'Qualification save error')
      // console.error(error);
      if (error?.response?.data?.qualifications)
        return setErrors(error?.response?.data?.qualifications[0]);
      setNotification("An error occurred while updating the form", "error");
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    console.log("Deleting qualification", id);
    setNotification("Qualification deleted", "success");
  };

  if (loading) return <CustomLoader />;

  const certificateUrl =
    import.meta.env.VITE_BACKEND_DOMAIN || "http://localhost:8000";

  const dialogTitle = selectedQualification
    ? "Edit Qualification"
    : "Add Qualification";

  return (
    <div>
      {/* Header with Add Button */}
      {qualifications?.length === 0 ? (
        <>
          {/* <TransitionAlert message="Add your educational background and aviation certifications. This information helps employers assess your qualifications." /> */}
          <EmptyQualificationCard handleOpen={handleOpen} />
        </>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
              mb: 3,
            }}
          >
            <Typography variant="h6">Qualifications</Typography>
            <Button
              startIcon={<Plus size={18} />}
              variant="contained"
              color="primary"
              onClick={() => handleOpen()}
            >
              Add qualification
            </Button>
          </Box>

          {/* Qualification List */}
          <Grid container spacing={3}>
            {qualifications.map((qualification) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={qualification.id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                    transition: "0.2s",
                    "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.15)" },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <CardContent>
                    {/* Education Level */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Box
                        sx={{
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 1,
                        }}
                      >
                        <GraduationCap size={18} style={{ marginRight: 8 }} />
                      </Box>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          mt: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "400px",
                        }}
                      >
                        Education Level:{" "}
                        {qualification.education_level_display ||
                          "Not Specified"}
                      </Typography>
                    </Box>

                    {/* Course of Study */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        sx={{
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 1,
                        }}
                      >
                        <BookOpen size={16} style={{ marginRight: 8 }} />
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "400px",
                        }}
                      >
                        Course:{" "}
                        {qualification.course_of_study || "Not specified"}
                      </Typography>
                    </Box>

                    {/* Institution */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        sx={{
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 1,
                        }}
                      >
                        <Building size={16} style={{ marginRight: 8 }} />
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "400px",
                        }}
                      >
                        Institution:{" "}
                        {qualification.institution || "Not specified"}
                      </Typography>
                    </Box>

                    {/* Graduation Year */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        sx={{
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 1,
                        }}
                      >
                        <Calendar size={16} style={{ marginRight: 8 }} />
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "400px",
                        }}
                      >
                        Graduation Year:{" "}
                        {qualification.expected_graduation_year ||
                          "Not specified"}
                      </Typography>
                    </Box>

                    {/* GPA */}
                    {/* <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        sx={{
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 1,
                        }}
                      >
                        <FileText size={16} style={{ marginRight: 8 }} />
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "400px",
                        }}
                      >
                        GPA: {qualification.gpa || "Not specified"}
                      </Typography>
                    </Box> */}

                    {/* Certificate Upload */}
                    {qualification.certificate_upload && (
                      <CertificatePreview
                        certificateUrl={certificateUrl}
                        filePath={qualification.certificate_upload}
                      />
                    )}
                  </CardContent>

                  {/* Actions */}
                  <CardActions sx={{ justifyContent: "flex-end" }}>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpen(qualification)}
                    >
                      <Pencil size={18} />
                    </IconButton>
                    {/* <IconButton
                  color="error"
                  onClick={() => handleDelete(qualification.id)}
                >
                  <Trash2 size={18} />
                </IconButton> */}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
      {/* Add/Edit Dialog */}
      <Modal handleClose={handleClose} open={open} title={dialogTitle}>
        <QualificationForm
          qualification={selectedQualification}
          onSubmit={handleSave}
          onCancel={handleClose}
          certificateUrl={certificateUrl}
          errors={errors}
          setErrors={setErrors}
          dropDownOptions={dropDownOptions}
        />
      </Modal>
    </div>
  );
};

export default QualificationsTab;
